import {
  EXPORT_POLL_MAX_ATTEMPTS,
  EXPORT_STATUS_META,
  exportDownloadUrl,
  formatBytes,
  signedUrlExpiryNote,
  type ExportUiState,
} from "../../lib/m4";

export interface ExportStatusProps {
  state: ExportUiState;
  onClose: () => void;
  onRetry: () => void;
}

/**
 * 导出任务状态卡（右下角常驻，不是 toast）。
 *
 * zip 要走后台打包，可能几十秒；用 toast 会 5 秒就没了，用户看不到下载链接。
 * 成功后给签名 URL + 过期提示（契约 M4 §3：TTL 1 小时）。
 */
export function ExportStatus({ state, onClose, onRetry }: ExportStatusProps) {
  if (state.phase === "idle") return null;

  const dismiss = (
    <button type="button" className="dp-icon-btn" aria-label="关闭导出状态" onClick={onClose}>
      ×
    </button>
  );

  if (state.phase === "creating") {
    return (
      <div className="dp-export-card" role="status" aria-live="polite">
        <div className="dp-export-card-head">
          <span className="dp-label">export · zip</span>
          {dismiss}
        </div>
        <p className="dp-export-card-title">正在创建导出任务…</p>
      </div>
    );
  }

  if (state.phase === "polling") {
    const label = EXPORT_STATUS_META[state.status].label;
    return (
      <div className="dp-export-card" role="status" aria-live="polite">
        <div className="dp-export-card-head">
          <span className="dp-label">export · zip</span>
          {dismiss}
        </div>
        <p className="dp-export-card-title">
          {label}
          <span className="dp-export-card-sub">
            {" "}
            已检查 {state.attempts}/{EXPORT_POLL_MAX_ATTEMPTS} 次
          </span>
        </p>
        <p className="dp-export-card-note">打包在后台进行，每 2 秒查询一次，可以继续改故事板。</p>
      </div>
    );
  }

  if (state.phase === "done") {
    const url = exportDownloadUrl(state.job);
    if (!url) {
      return (
        <div className="dp-export-card is-bad" role="alert">
          <div className="dp-export-card-head">
            <span className="dp-label">export · zip</span>
            {dismiss}
          </div>
          <p className="dp-export-card-title">导出完成，但没有拿到下载链接</p>
          <p className="dp-export-card-note">请重新导出；若持续出现，请联系管理员。</p>
          <button type="button" className="dp-btn sm" onClick={onRetry}>
            重新导出
          </button>
        </div>
      );
    }

    return (
      <div className="dp-export-card is-done" role="status" aria-live="polite">
        <div className="dp-export-card-head">
          <span className="dp-label">export · zip</span>
          {dismiss}
        </div>
        <p className="dp-export-card-title">
          导出完成
          {state.job.bytes !== null ? (
            <span className="dp-export-card-sub"> · {formatBytes(state.job.bytes)}</span>
          ) : null}
        </p>
        <div className="dp-export-card-actions">
          <a
            className="dp-btn sm primary"
            href={url}
            target="_blank"
            rel="noreferrer"
            download
            title="签名下载链接（1 小时有效）"
          >
            下载 zip
          </a>
          <button type="button" className="dp-btn sm" onClick={onRetry}>
            重新导出
          </button>
        </div>
        <p className="dp-export-card-note">{signedUrlExpiryNote(state.job.expiresAt)}</p>
      </div>
    );
  }

  const forbidden = state.phase === "forbidden";
  return (
    <div className={`dp-export-card${forbidden ? " is-bad" : " is-error"}`} role="alert">
      <div className="dp-export-card-head">
        <span className="dp-label">export · zip</span>
        {dismiss}
      </div>
      <p className="dp-export-card-title">
        {forbidden ? "当前套餐不含导出" : state.phase === "timeout" ? "导出还没完成" : "导出失败"}
      </p>
      <p className="dp-export-card-note">{state.message}</p>
      {forbidden ? null : (
        <button type="button" className="dp-btn sm" onClick={onRetry}>
          重试
        </button>
      )}
    </div>
  );
}
